import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LoadingState,
  type SliceProps,
  type SliceRequestInput,
  type SliceResponse,
} from './types';

const useSliceRequest = <
  Props extends { slice: SliceRequestInput },
  Response extends SliceResponse,
>(
  request: (props: Props) => Promise<Response>,
  props?: Partial<Omit<Props, 'slice'> & { slice: SliceProps }>,
): {
  loading: LoadingState;
  error?: Error;
  data: Response['data'];
  loadMore: () => void;
  hasNext: boolean;
} => {
  const requestRef = useRef(request);
  const sliceInfo = useRef<
    SliceRequestInput & {
      loading: LoadingState;
    }
  >({
    currentSlice: 0,
    itemsPerSlice: props?.slice?.itemsPerSlice ?? 10,
    loading: LoadingState.initial,
  });
  const [loading, setLoading] = useState(LoadingState.initial);
  const [data, setData] = useState<Response['data']>([]);
  const [error, setError] = useState<Error | undefined>();
  const [hasNext, setHasNext] = useState(true);

  useEffect(() => {
    sliceInfo.current.currentSlice = 0;
    setData([]);
    void props;
  }, [props]);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const loadMore = useCallback(() => {
    if (sliceInfo.current.loading === LoadingState.loading) {
      return;
    }
    setLoading(LoadingState.loading);
    sliceInfo.current.loading = LoadingState.loading;
    requestRef
      .current({
        ...props,
        slice: {
          currentSlice: sliceInfo.current.currentSlice,
          itemsPerSlice: sliceInfo.current.itemsPerSlice,
        },
      } as Props)
      .then(({ data, slice }) => {
        const hasNext =
          slice.hasNext ?? data.length === sliceInfo.current.itemsPerSlice;
        setHasNext(hasNext);

        sliceInfo.current.currentSlice = sliceInfo.current.currentSlice + 1;

        if (data.length > 0) {
          setData((prevData) => [...prevData, ...data]);
        }
        setLoading(LoadingState.success);
        setError(undefined);
        sliceInfo.current.loading = LoadingState.success;
      })
      .catch((error) => {
        setError(error);
        setLoading(LoadingState.failed);
        sliceInfo.current.loading = LoadingState.failed;
        setHasNext(false);
      });
  }, [props]);

  return {
    loading,
    error,
    data,
    loadMore,
    hasNext,
  };
};

export default useSliceRequest;
